<template>
  <RouterView></RouterView>
</template>

<script setup lang="ts">
import { useSentry } from '@staffingengine/common'
import { authHooks } from '@/composition/database-client'
import { useMixpanel } from '@staffingengine/common'

const route = useRoute()
const router = useRouter()

//
// User State
//

const auth = useAuth()
const userProfilesStore = useUserProfilesStore()
const organizationStore = useOrganizationStore()

/**
 * Fetch the appropriate user profile and organization when metadata for the signed in user changes
 */
watchEffect(async () => {
  const authMeta = auth.userMetadata.value

  if (authMeta?.user) {
    await userProfilesStore.fetchById(authMeta.user)
  }

  if (authMeta?.organization) {
    await organizationStore.fetchById(authMeta.organization)
  }
})

const subscribeToOrgChanges = () =>
  organizationStore.$subscribe((mutation, state) => {
    if (!state.organization) return

    useMixpanel().people.set_once({
      Organization: state.organization.name,
      'Organization Id': state.organization.id,
    })
    useMixpanel().add_group('organization', state.organization.name)
  })

const subscribeToUserChanges = () =>
  userProfilesStore.$subscribe((mutation, state) => {
    if (!state.signedInUser) return

    useMixpanel().people.set_once({
      'User Id': state.signedInUser.id,
    })
    useMixpanel().people.set({
      $name: `${state.signedInUser.first_name} ${state.signedInUser.last_name}`,
      Role: state.signedInUser.role,
    })
    useMixpanel().add_group('role', state.signedInUser.role)
  })

const authHookRegistrations: Array<() => void> = []

/**
 * Perform setup actions on sign in/out
 */
const registerAuthHooks = () => {
  const signIn = authHooks.hook('sign-in', async ({ session }) => {
    useSentry().addBreadcrumb({
      type: 'info',
      level: 'info',
      category: 'auth',
      message: 'Sign In',
      data: {
        expires_in: session?.expires_in,
        user: session?.user.id,
        email: session?.user?.email,
      },
    })

    if (route.name === 'auth-signin') {
      // TODO: support redirects
      await router.push({ name: 'index' })
    }
  })

  const signOut = authHooks.hook('sign-out', () => {
    useSentry().addBreadcrumb({
      type: 'info',
      level: 'info',
      category: 'auth',
      message: 'Sign Out',
    })

    auth.reset()
    userProfilesStore.reset()
    organizationStore.reset()
  })

  authHookRegistrations.push(signIn, signOut)
}

const unregisterAuthHooks = () => {
  for (const unregister of authHookRegistrations) {
    unregister()
  }
}

onMounted(async () => {
  registerAuthHooks()
  subscribeToOrgChanges()
  subscribeToUserChanges()
  await auth.init()
})

onUnmounted(() => {
  unregisterAuthHooks()
})
</script>
